import type { Role, UserListItem } from '@aurea/contracts';
import { Button, Chip, Dialog, Field, Input, Seg, Select, useToast } from '@aurea/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { FileUp, Pencil, Plus, Search } from 'lucide-react';
import { type FormEvent, useDeferredValue, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImportStudents } from '../../components/ImportStudents';
import { UserDialog } from '../../components/UserDialog';
import { ApiError, api } from '../../lib/api';
import { useErrorText } from '../../lib/i18n';
import { fmtDate } from '../../lib/time';
import { canManage, isAdmin, useMe } from '../_app';

export const Route = createFileRoute('/_app/people/')({ component: People });

function People() {
  const me = useMe();
  const { t, i18n } = useTranslation();
  const [role, setRole] = useState<Role | 'all'>('student');
  const [q, setQ] = useState('');
  const dq = useDeferredValue(q.trim());
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [status, setStatus] = useState<UserListItem['status']>('active');
  const [editing, setEditing] = useState<UserListItem | null>(null);
  const admin = isAdmin(me);
  const manage = canManage(me);
  const users = useQuery({
    queryKey: ['users', role, dq, status],
    queryFn: () => api.users({ role: role === 'all' ? undefined : role, q: dq || undefined, status }),
    placeholderData: (p) => p,
  });

  return (
    <>
      <header className="page-head">
        <h1>{t('people.title')}</h1>
        <div className="flex-1" />
        {manage && (
          <Button variant="ghost" onClick={() => setImporting(true)}>
            <FileUp className="i" />
            {t('import.button')}
          </Button>
        )}
        {manage && (
          <Button onClick={() => setCreating(true)}>
            <Plus className="i" />
            {t('people.add')}
          </Button>
        )}
      </header>
      <section className="tray overflow-hidden">
        <div className="cal-bar">
          <Seg<Role | 'all'>
            label={t('people.roles')}
            value={role}
            onChange={setRole}
            options={[
              { value: 'student', label: t('roles.student') },
              { value: 'teacher', label: t('roles.teacher') },
              { value: 'admin', label: t('roles.admin') },
              { value: 'all', label: t('people.all') },
            ]}
          />
          {admin && (
            <Select
              pill
              aria-label={t('users.show')}
              value={status}
              onChange={(e) => setStatus(e.target.value as UserListItem['status'])}
            >
              <option value="active">{t('users.status.active')}</option>
              <option value="blocked">{t('users.status.blocked')}</option>
              <option value="archived">{t('users.status.archived')}</option>
            </Select>
          )}
          {/* biome-ignore lint/a11y/noLabelWithoutControl: Input из @aurea/ui — это <input> */}
          <label className="relative ml-auto w-full max-w-xs">
            <Search
              className="i pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-3"
              aria-hidden="true"
            />
            <Input
              className="!rounded-full !py-1.5 pl-9"
              placeholder={t('people.search')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label={t('common.search')}
            />
          </label>
        </div>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>{t('people.name')}</th>
                <th className="hide-mobile">{t('people.email')}</th>
                <th>{t('people.courses')}</th>
                <th className="hide-mobile">{t('people.lastLogin')}</th>
                {manage && (
                  <th>
                    <span className="sr-only">{t('users.edit')}</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {users.data?.map((u) => (
                <tr key={u.id}>
                  <td>
                    {isAdmin(me) && u.roles.includes('student') ? (
                      <Link to="/people/$id" params={{ id: u.id }} className="font-medium hover:underline">
                        {u.lastName} {u.firstName}
                      </Link>
                    ) : (
                      <div className="font-medium">
                        {u.lastName} {u.firstName}
                      </div>
                    )}
                    <div className="text-xs text-ink-3">
                      {u.roles.map((r) => t(`roles.${r}`)).join(', ')}
                      {u.isOwner && ` · ${t('people.owner')}`}
                    </div>
                  </td>
                  <td className="hide-mobile text-ink-2">{u.email}</td>
                  <td>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      {u.courses.slice(0, 3).map((c) => (
                        <span key={c.name} className="inline-flex items-center gap-1.5 whitespace-nowrap">
                          <Chip color={c.color} small />
                          {c.name}
                        </span>
                      ))}
                      {u.courses.length > 3 && <span className="text-ink-3">+{u.courses.length - 3}</span>}
                    </div>
                  </td>
                  <td className="hide-mobile text-ink-3">
                    {u.lastLoginAt ? fmtDate(u.lastLoginAt, me.timezone, i18n.language) : t('people.never')}
                  </td>
                  {manage && (
                    <td className="w-12 text-right">
                      <button
                        type="button"
                        className="btn btn-ghost"
                        aria-label={t('users.editName', { name: `${u.firstName} ${u.lastName}` })}
                        onClick={() => setEditing(u)}
                      >
                        <Pencil className="i" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {users.data?.length === 0 && <p className="p-6 text-sm text-ink-3">{t('people.empty')}</p>}
          {users.isPending && <div className="skeleton m-6 h-40" />}
        </div>
      </section>
      {importing && <ImportStudents onClose={() => setImporting(false)} />}
      {editing && <UserDialog user={editing} self={editing.id === me.id} onClose={() => setEditing(null)} />}
      {creating && (
        <CreateUser onClose={() => setCreating(false)} defaultRole={role === 'all' ? 'student' : role} />
      )}
    </>
  );
}

function CreateUser({ onClose, defaultRole }: { onClose: () => void; defaultRole: Role }) {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const toast = useToast();
  const qc = useQueryClient();
  const [link, setLink] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: api.createUser,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['me'] });
      setLink(r.setupUrl);
    },
  });
  const fields =
    create.error instanceof ApiError
      ? (create.error.details?.fields as Record<string, string[]> | undefined)
      : undefined;
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const v = (k: string) => String(f.get(k) ?? '').trim();
    create.mutate({
      firstName: v('firstName'),
      lastName: v('lastName'),
      email: v('email'),
      phone: v('phone') || undefined,
      roles: [v('role') as Role],
    });
  };
  return (
    <Dialog open onClose={onClose} title={t('people.formTitle')} strip="mint">
      {link ? (
        <div className="pb-6">
          <p className="text-sm text-ink-2">{t('people.created')}</p>
          <div className="mt-3 flex gap-2">
            <Input readOnly value={link} onFocus={(e) => e.currentTarget.select()} />
            <Button
              variant="ghost"
              onClick={() => {
                navigator.clipboard?.writeText(link);
                toast(t('common.copied'));
              }}
            >
              {t('common.copy')}
            </Button>
          </div>
          <Button className="mt-4" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      ) : (
        <form className="flex flex-col gap-4 pb-6" onSubmit={submit}>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('people.firstName')} error={fields?.firstName && t('errors.VALIDATION_FAILED')}>
              <Input name="firstName" required autoFocus />
            </Field>
            <Field label={t('people.lastName')}>
              <Input name="lastName" required />
            </Field>
          </div>
          <Field label={t('people.email')} error={fields?.email && t('errors.VALIDATION_FAILED')}>
            <Input name="email" type="email" required />
          </Field>
          <Field label={`${t('people.phone')} (${t('common.optional')})`}>
            <Input name="phone" type="tel" />
          </Field>
          <Field label={t('people.role')}>
            <Select name="role" defaultValue={defaultRole}>
              <option value="student">{t('roles.student')}</option>
              <option value="teacher">{t('roles.teacher')}</option>
              <option value="admin">{t('roles.admin')}</option>
            </Select>
          </Field>
          {create.isError && !fields && <p className="text-sm text-danger">{errorText(create.error)}</p>}
          <div className="flex gap-3">
            <Button type="submit" loading={create.isPending}>
              {t('common.create')}
            </Button>
            <Button variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
